import { useState } from "react";

import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { CgAdd } from "react-icons/cg";

import RowContext from "../context/RowContext";

import DisplayRows from "./DisplayRows";
import TableHeader from "./TableHeader";

import "../styles/Table.css";

function Table() {
  const [standardsList, setStandardsList] = useState([]); //hook to control overall list

  const [isCreation, setIsCreation] = useState(false); //for adding new row text

  const [inputText, setInputText] = useState(""); //for changing input text

  const [isEditing, setIsEditing] = useState(false); //for checking if editing is in progress

  const [currentIndex, setCurrentIndex] = useState(); //for row index when editing in progress

  const returnFontStyle = (nestedLevel) => {
    let fontStyle;

    switch (nestedLevel) {
      case 0:
        fontStyle = {
          color: "darkturquoise",
          fontWeight: "bold",
        };
        break;
      case 1:
        fontStyle = {
          color: "black",
          fontWeight: "bold",
        };
        break;
      case 2:
        fontStyle = {
          color: "grey",
          fontWeight: "700",
        };
        break;
      case 3:
        fontStyle = {
          color: "lightgrey",
          fontWeight: "bold",
        };
        break;
      default:
        fontStyle = {
          color: "black",
          fontWeight: "normal",
        };
    }

    return fontStyle;
  };

  const generateUID = () => {
    const num = Math.random();
    return num.toString().slice(4);
  };

  //To create a new row on 'Add' button click
  const onButtonClick = () => {
    if (isCreation === false && isEditing === false) {
      setIsCreation(true);

      const previousID = standardsList.length; //set indent acc to previous

      const previousNestedLevel =
        previousID === 0 ? 0 : standardsList[previousID - 1].nestedLevel;

      const newStandard = {
        id: generateUID(),
        nestedLevel: previousNestedLevel,
        text: "",
      };

      setStandardsList([...standardsList, newStandard]);
    }
  };

  //To indent a selected row
  const indentOnClick = (id) => {
    if (id !== 0) {
      let newList = [...standardsList];

      const maxLevel = newList[id - 1].nestedLevel + 1; //set max limit acc to prev
      const newLevel = newList[id].nestedLevel + 1;

      newList[id].nestedLevel = newLevel > maxLevel ? maxLevel : newLevel;

      setStandardsList(newList);
    }
  };

  //To outdent a selected row
  const outdentOnClick = (id) => {
    if (id !== 0 && standardsList[id].nestedLevel !== 0) {
      let newList = [...standardsList];

      const newLevel = newList[id].nestedLevel - 1;
      newList[id].nestedLevel = newLevel;

      setStandardsList(newList);
    }
  };

  //To delete a row and its sub-rows
  const deleteOnClick = (id) => {
    let intactList = [...standardsList];

    const v = intactList[id].nestedLevel;
    let count = 1;

    const deletionList = intactList.splice(id);

    if (deletionList.length > 0) {
      //count until nestedLevel !== nestedLevel of first (parent) node
      let id = 1;
      while (id < deletionList.length && deletionList[id++].nestedLevel > v) {
        ++count;
      }
    }

    deletionList.splice(0, count);

    deletionList.length === 0
      ? setStandardsList(intactList)
      : setStandardsList([...intactList, ...deletionList]);
  };

  //To confirm an edited text or add text in a new row
  const enterKeyPressed = (e, id) => {
    if (e.key === "Enter") {
      let newList = [...standardsList];

      newList[id].text = inputText;

      setStandardsList(newList);
      setIsCreation(false);
      setInputText("");
      setIsEditing(false);
      setCurrentIndex();
    }
  };

  const onInputChange = (e) => {
    const currentInput = e.target.value;
    setInputText(currentInput);
  };

  //To set inputText to existing text in a row being edited
  const editStandardText = (id) => {
    if (isEditing === false) {
      setCurrentIndex(id);
      setIsEditing(true);
      setInputText(standardsList[id].text);
    }
  };

  //To perform reordering of list when element is dropped
  const onDragEnd = (result) => {
    if (!result.destination) {
      return; //if outside droppable area
    }

    const newList = [...standardsList];

    const sourceIndex = result.source.index;

    let count = 1,
      id = sourceIndex + 1;

    const v = sourceIndex === 0 ? 0 : newList[sourceIndex].nestedLevel;

    while (id < newList.length && newList[id].nestedLevel > v) {
      ++count;
      ++id;
    }

    const dragged = newList.splice(result.source.index, count);

    newList.splice(result.destination.index, 0, ...dragged);

    newList[0].nestedLevel = 0; //make first element the parent

    let traversingIndex = 1;
    while (traversingIndex < newList.length) {
      if (
        newList[traversingIndex].nestedLevel >
        newList[traversingIndex - 1].nestedLevel + 1
      ) {
        newList[traversingIndex].nestedLevel =
          newList[traversingIndex - 1].nestedLevel + 1; //snap to parent component
      }
      ++traversingIndex;
    }
    setStandardsList(newList);
  };

  return (
    <RowContext.Provider
      value={{
        currentIndex: currentIndex,
        inputText: inputText,
        isEditing: isEditing,
        isCreation: isCreation,

        onInputChange: onInputChange,
        returnFontStyle: returnFontStyle,
        editStandardText: editStandardText,
        enterKeyPressed: enterKeyPressed,
        deleteOnClick: deleteOnClick,
        indentOnClick: indentOnClick,
        outdentOnClick: outdentOnClick,
      }}
    >
      <div className="table">
        <h3>Nested ToDo Lists</h3>
        <div className="table-divider"></div>
        <TableHeader />
        <div className="table-divider"></div>
        {standardsList.length === 0 && (
          <p className="table__empty-text">No todos added. Add them now!</p>
        )}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable-row-container">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {/* List constructor goes here */}
                {standardsList.length > 0 &&
                  standardsList.map((standard, idx) => {
                    return (
                      <Draggable
                        key={standard.id}
                        draggableId={standard.id}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {/* element goes here*/}
                            <DisplayRows
                              currentId={idx}
                              currentStandard={standard}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <button className="add-button" onClick={onButtonClick}>
          <CgAdd
            size={15}
            style={{ verticalAlign: "middle", position: "relative", bottom: 2 }}
          />{" "}
          Add A ToDo
        </button>
      </div>
    </RowContext.Provider>
  );
}

export default Table;
